import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './About.css';

const RTL_LANGUAGE_PREFIXES = ['ar', 'fa', 'he', 'ur', 'ps', 'sd', 'ckb'];

const getDirection = (language = 'en') => {
  const normalizedLanguage = String(language).trim().toLowerCase();

  if (
    RTL_LANGUAGE_PREFIXES.some(
      (prefix) =>
        normalizedLanguage === prefix ||
        normalizedLanguage.startsWith(`${prefix}-`) ||
        normalizedLanguage.startsWith(`${prefix}_`)
    ) ||
    normalizedLanguage.includes('arabic') ||
    normalizedLanguage.includes('farsi') ||
    normalizedLanguage.includes('persian') ||
    normalizedLanguage.includes('hebrew') ||
    normalizedLanguage.includes('urdu') ||
    normalizedLanguage.includes('pashto') ||
    normalizedLanguage.includes('sindhi') ||
    normalizedLanguage.includes('kurdish')
  ) {
    return 'rtl';
  }

  return 'ltr';
};

const US_MARKET_METRICS = [
  {
    id: 'household-real-estate',
    value: '$52.99T',
    title: 'U.S. household and nonprofit real estate',
    description:
      'Market value reported for the first quarter of 2026. This measure demonstrates the depth of the underlying U.S. real estate base, but it is not the same as the immediately tokenizable market.',
    source: 'Federal Reserve, Financial Accounts of the United States, 2026 Q1',
  },
  {
    id: 'commercial-real-estate',
    value: '$20.7T',
    title: 'U.S. commercial real estate benchmark',
    description:
      'Nareit estimated the U.S. commercial real estate market at this level in the second quarter of 2021. It remains a useful scale benchmark, although current sector values vary by property type and market.',
    source: 'Nareit commercial real estate market estimate',
  },
  {
    id: 'farm-real-estate',
    value: '$3.67T',
    title: 'U.S. farm real estate',
    description:
      'Forecast value of U.S. farm land and structures in 2025. Land-based assets are a major potential category, subject to title, valuation, use, transfer, and offering requirements.',
    source: 'USDA Economic Research Service, updated May 2026',
  },
  {
    id: 'rental-households',
    value: '46.1M',
    title: 'Renter-occupied U.S. housing units',
    description:
      'The 2024 American Community Survey estimated more than forty-six million renter-occupied housing units, showing the breadth of the rental-income ecosystem.',
    source: 'U.S. Census Bureau, 2024 American Community Survey',
  },
];

const US_GROWTH_SIGNALS = [
  {
    id: 'deloitte-forecast',
    value: '$4T',
    title: 'Tokenized real estate by 2035',
    description:
      'Deloitte forecasts that the global tokenized real estate ecosystem could grow from less than $0.3 trillion in 2024 to $4 trillion by 2035.',
  },
  {
    id: 'deloitte-cagr',
    value: '27%',
    title: 'Illustrative annual growth rate',
    description:
      'Deloitte associates its real estate tokenization forecast with an estimated compound annual growth rate of approximately twenty-seven percent.',
  },
  {
    id: 'mckinsey-tokenization',
    value: '$2T',
    title: 'Broader tokenized market by 2030',
    description:
      'McKinsey estimates that tokenized market capitalization could reach approximately $2 trillion by 2030, excluding cryptocurrencies and stablecoins.',
  },
  {
    id: 'regulatory-direction',
    value: '2026',
    title: 'Greater U.S. regulatory clarity',
    description:
      'The SEC has published current guidance explaining that a tokenized security remains subject to federal securities laws regardless of its on-chain or off-chain format.',
  },
];

const US_PLATFORM_PILLARS = [
{
  id: 'global-phased-launch',
  title: 'Global platform with phased market entry',
  description:
    'SRWAE is designed for international use, with the initial launch focused on the United States and a limited number of selected countries. Availability, user roles, services, and transaction workflows are determined by the applicable requirements in each jurisdiction.',
},
  {
    id: 'full-lifecycle',
    title: 'Full asset and token lifecycle',
    description:
      'SRWAE connects asset intake, due diligence, data rooms, legal review, valuation, escrow, offering preparation, investor access, reporting, distributions, and approved transfers.',
  },
  {
    id: 'role-based',
    title: 'Role-based access and responsibility',
    description:
      'Public visitors, verified investors, sponsors, issuers, operators, broker-dealers, transfer agents, escrow users, legal reviewers, and other professionals receive different permissions.',
  },
  {
    id: 'no-platform-stablecoin',
    title: 'No required platform stablecoin',
    description:
      'The platform is designed around real-world asset workflows and approved payment or settlement arrangements rather than requiring a proprietary stablecoin.',
  },
];

const US_TOKENIZATION_MODELS = [
  {
    id: 'rental-income',
    code: 'RI',
    title: 'Rental Income',
    description:
      'Represents an approved economic interest connected to rental cash flow, subject to the offering documents, operating expenses, reserves, property performance, and distribution rules.',
  },
  {
    id: 'revenue-share',
    code: 'RS',
    title: 'Revenue Share',
    description:
      'Connects investor economics to an agreed share of qualifying project or property revenue rather than direct ownership of the underlying real estate.',
  },
  {
    id: 'sale-profit',
    code: 'SP',
    title: 'Sale Profit',
    description:
      'Supports projects in which the primary economic objective is a defined share of net profit after an approved property sale and related costs.',
  },
  {
    id: 'equity-appreciation',
    code: 'EA',
    title: 'Equity Appreciation',
    description:
      'Represents an approved participation structure connected to increases or decreases in property or project equity under the controlling agreements.',
  },
  {
    id: 'preferred-equity',
    code: 'PE',
    title: 'Preferred Equity',
    description:
      'Supports a preferred economic position with defined priority, return mechanics, limitations, and risk disclosures established by the issuer and legal documents.',
  },
  {
    id: 'common-equity',
    code: 'CE',
    title: 'Common Equity',
    description:
      'Supports a common ownership interest in an approved entity or series, including applicable voting, distribution, dilution, and transfer provisions.',
  },
  {
    id: 'debt-note',
    code: 'DN',
    title: 'Debt Note',
    description:
      'Represents an approved debt obligation with documented principal, interest, maturity, collateral, payment priority, default, and enforcement terms.',
  },
  {
    id: 'hybrid',
    code: 'HY',
    title: 'Hybrid',
    description:
      'Combines two or more approved economic components, such as debt, preferred return, revenue share, rental income, or appreciation participation.',
  },
  {
    id: 'pre-auction-pool',
    code: 'PA',
    title: 'Pre-Auction Pool',
    description:
      'Organizes approved capital commitments and acquisition workflows before an auction, with eligibility, bidding authority, escrow, refund, and allocation rules.',
  },
  {
    id: 'post-auction-token',
    code: 'PO',
    title: 'Post-Auction Token',
    description:
      'Supports an acquired auction asset after purchase, including title review, rehabilitation, stabilization, refinancing, rental, sale, and investor reporting workflows.',
  },
];

const US_INITIAL_MARKET_SEGMENTS = [
  {
    id: 'documented-rentals',
    title: 'Documented rental portfolios',
    description:
      'Small and midsize rental portfolios with clear ownership, leases, operating history, insurance, expenses, reserves, and property management records can provide an understandable first use case.',
  },
  {
    id: 'land-construction',
    title: 'Land and construction projects',
    description:
      'Undeveloped land and construction projects may use milestone-based escrow, independent inspection, controlled releases, and progress reporting to improve transparency.',
  },
  {
    id: 'auction-opportunities',
    title: 'Pre-auction acquisition opportunities',
    description:
      'Approved acquisition entities can organize time-sensitive capital, bidding limits, escrow instructions, refund rules, and post-acquisition allocation before an auction.',
  },
  {
    id: 'post-auction-rehab',
    title: 'Post-auction rehabilitation',
    description:
      'Recently acquired properties may need title resolution, repair funding, inspections, stabilization, leasing, refinancing, or sale before a complete investment strategy is established.',
  },
  {
    id: 'external-token-review',
    title: 'Existing external token review',
    description:
      'Issuers that already created a token elsewhere can request document review, data-room support, display, reporting, or restricted listing workflows without assuming automatic approval.',
  },
];

const US_CAPACITY_SCENARIOS = [
  {
    id: 'controlled-pilot',
    stage: 'Controlled pilot',
    activeRecords: '25',
    averageValue: '$1M',
    representedValue: '$25M',
    meaning:
      'A small launch used to validate legal templates, data quality, partner workflows, investor onboarding, reporting, security, and operational controls.',
  },
  {
    id: 'regional-launch',
    stage: 'Regional launch',
    activeRecords: '250',
    averageValue: '$1.5M',
    representedValue: '$375M',
    meaning:
      'A multi-partner operation serving several asset owners, property types, professional reviewers, and approved investor groups in selected states.',
  },
  {
    id: 'multi-state-scale',
    stage: 'Multi-state scale',
    activeRecords: '2,500',
    averageValue: '$2M',
    representedValue: '$5B',
    meaning:
      'A scaled service with standardized onboarding, stronger automation, multiple legal and transaction partners, portfolio reporting, and mature support operations.',
  },
  {
    id: 'national-platform-scale',
    stage: 'National platform scale',
    activeRecords: '10,000',
    averageValue: '$3M',
    representedValue: '$30B',
    meaning:
      'A national operating scenario requiring enterprise architecture, redundancy, independent controls, extensive partner capacity, and state-by-state legal and operational coverage.',
  },
];

const US_WORKFLOW_STEPS = [
  {
    id: 'discover',
    number: '01',
    title: 'Discover and classify',
    description:
      'Identify the asset, ownership entity, jurisdiction, intended economics, project stage, users, documents, and requested tokenization model.',
  },
  {
    id: 'verify',
    number: '02',
    title: 'Verify ownership and data',
    description:
      'Collect and review title, entity, financial, appraisal, lease, auction, construction, insurance, tax, identity, and source records as applicable.',
  },
  {
    id: 'structure',
    number: '03',
    title: 'Structure the legal interest',
    description:
      'Define the issuer, SPV or series, investor rights, exemptions, restrictions, disclosures, transfer rules, governance, distributions, and risk allocation.',
  },
  {
    id: 'approve',
    number: '04',
    title: 'Complete professional review',
    description:
      'Route the project to assigned legal, compliance, valuation, title, escrow, inspection, financial, tax, and operational reviewers.',
  },
  {
    id: 'publish',
    number: '05',
    title: 'Prepare and publish the offering',
    description:
      'Create the approved listing, data room, map view, token record, risk information, documents, investor eligibility rules, and subscription workflow.',
  },
  {
    id: 'operate',
    number: '06',
    title: 'Operate and report',
    description:
      'Track funding, escrow, milestones, property activity, income, expenses, reserves, inspections, distributions, documents, and investor communications.',
  },
  {
    id: 'transfer',
    number: '07',
    title: 'Control approved transfers',
    description:
      'Apply holder eligibility, legal restrictions, transfer-agent records, broker-dealer support, approvals, tax records, and audit trails to permitted transfers.',
  },
];

const US_ROLE_GROUPS = [
  {
    id: 'market-users',
    title: 'Market and investor users',
    items: [
      'Public visitor',
      'Verified investor',
      'Sponsor administrator',
      'Offering administrator',
      'Marketplace administrator',
    ],
  },
  {
    id: 'project-users',
    title: 'Project-side users',
    items: [
      'Asset owner',
      'Sponsor',
      'Issuer',
      'Property manager',
      'Asset manager',
      'Rehabilitation manager',
    ],
  },
  {
    id: 'transaction-users',
    title: 'Transaction and custody users',
    items: [
      'Broker-dealer signer',
      'Transfer agent',
      'Token operations user',
      'Escrow agent',
      'Escrow administrator',
    ],
  },
  {
    id: 'professional-users',
    title: 'Professional and control users',
    items: [
      'Legal counsel',
      'Securities attorney',
      'Compliance reviewer',
      'Title reviewer',
      'Valuation reviewer',
      'Inspector',
      'Financial analyst',
      'Tax and audit professional',
      'Platform and map data administrator',
    ],
  },
];

const US_ACCESS_LEVELS = [
  {
    id: 'public-access',
    label: 'Public access',
    value: 'Free and limited',
    description:
      'Public users may view limited listing information and approximate map information without receiving precise locations, complete documents, review requests, or transaction access.',
  },
  {
    id: 'verified-access',
    label: 'Verified access',
    value: 'Role and eligibility based',
    description:
      'Verified and approved users may receive broader access according to identity, eligibility, jurisdiction, offering terms, agreements, and assigned permissions.',
  },
  {
    id: 'project-services',
    label: 'Project-side SaaS service',
    value: 'Monthly service plans',
    description:
      'Asset owners, sponsors, issuers, and operators can select a service plan for active asset, token, listing, data, reporting, and workflow support.',
  },
  {
    id: 'transaction-services',
    label: 'Approved transaction service',
    value: '0.5% after a completed buy or sell',
    description:
      'The current service model applies this fee only to an approved broker-dealer signer, transfer agent, or token operations user under applicable agreements and legal requirements.',
  },
];

const US_MARKET_DRIVERS = [
  {
    id: 'fractional-access',
    title: 'Smaller investment units',
    description:
      'A properly structured fractional interest can reduce the minimum capital required for an individual investor, although investor eligibility and offering limits still apply.',
  },
  {
    id: 'capital-flexibility',
    title: 'More flexible capital formation',
    description:
      'Sponsors may seek capital against a defined economic interest without necessarily selling the entire asset, but the transaction must follow its legal structure and disclosures.',
  },
  {
    id: 'operational-transparency',
    title: 'More visible operating records',
    description:
      'Standardized data rooms, review records, milestone evidence, distribution reports, and transfer histories can improve transparency when the underlying data is accurate and verified.',
  },
  {
    id: 'programmable-controls',
    title: 'Programmable transfer controls',
    description:
      'Approved systems can support wallet allowlists, transfer restrictions, holding rules, identity checks, and synchronized on-chain and off-chain records.',
  },
  {
    id: 'portfolio-discovery',
    title: 'Better asset discovery',
    description:
      'Search, maps, standardized token codes, risk indicators, document access, and comparable records can help approved users evaluate opportunities more efficiently.',
  },
  {
    id: 'secondary-transfer-potential',
    title: 'Potential for controlled secondary transfers',
    description:
      'Tokenization may make approved transfers easier to administer, but it does not guarantee a secondary market, buyers, price stability, or immediate liquidity.',
  },
];

const US_RISK_CONTROLS = [
  'Tokenization does not change the legal nature of a security or eliminate registration, exemption, disclosure, custody, recordkeeping, anti-fraud, and investor-protection requirements.',
  'A token does not by itself prove title, ownership, valuation, income, collateral, legal compliance, or enforceability of the represented interest.',
  'Listings, reviews, approvals, risk labels, inspections, and documents reduce information gaps but cannot eliminate investment, property, construction, auction, market, technology, or counterparty risk.',
  'Liquidity, distributions, appreciation, rental income, refinancing, resale, auction success, and investment returns are never guaranteed.',
  'Each U.S. offering may require federal and state legal analysis, investor eligibility controls, KYC and AML procedures, tax review, transfer restrictions, custody or transfer-agent support, and separate agreements.',
];

const US_SOURCES = [
  {
    id: 'federal-reserve',
    title: 'Federal Reserve Financial Accounts of the United States, 2026 Q1',
    note: 'Household and nonprofit real estate market value and related U.S. balance-sheet context.',
    href: 'https://www.federalreserve.gov/releases/z1/current/html/S1_b.htm',
  },
  {
    id: 'usda',
    title: 'USDA Economic Research Service, Farmland Value',
    note: 'U.S. farm real estate value, land values, and recent growth information.',
    href: 'https://ers.usda.gov/topics/farm-economy/land-use-land-value-tenure/farmland-value',
  },
  {
    id: 'census',
    title: 'U.S. Census Bureau, 2024 American Community Survey, Table B25003',
    note: 'National owner-occupied and renter-occupied housing unit estimates.',
    href: 'https://data.census.gov/table/ACSDT1Y2024.B25003?g=010XX00US',
  },
  {
    id: 'nareit',
    title: 'Nareit estimate of the U.S. commercial real estate market',
    note: 'A commercial real estate scale benchmark based on the second quarter of 2021.',
    href: 'https://www.reit.com/news/blog/market-commentary/estimating-size-commercial-real-estate-market',
  },
  {
    id: 'deloitte',
    title: 'Deloitte, Tokenized Real Estate',
    note: 'Forecast for the global tokenized real estate ecosystem through 2035.',
    href: 'https://www.deloitte.com/us/en/insights/industry/financial-services/financial-services-industry-predictions/2025/tokenized-real-estate.html',
  },
  {
    id: 'mckinsey',
    title: 'McKinsey, From Ripples to Waves',
    note: 'Forecast and adoption framework for broader tokenized financial assets through 2030.',
    href: 'https://www.mckinsey.com/industries/financial-services/our-insights/from-ripples-to-waves-the-transformational-power-of-tokenizing-assets',
  },
  {
    id: 'sec',
    title: 'U.S. Securities and Exchange Commission, Statement on Tokenized Securities',
    note: 'Current explanation of issuer-sponsored and third-party tokenization models and federal securities-law treatment.',
    href: 'https://www.sec.gov/newsroom/speeches-statements/corp-fin-statement-tokenized-securities-012826-statement-tokenized-securities',
  },
];

const UAE_PLATFORM_PILLARS = [
  {
    "id": "uae-pillar-1",
    "title": "UAE platform with jurisdiction-specific market entry",
    "description": "SRWAE UAE uses one software and data standard while routing each project through the applicable UAE pathway. Dubai outside DIFC, DIFC, ADGM, and other Emirates can require different authorities, permissions, property records, and licensed roles."
  },
  {
    "id": "uae-pillar-2",
    "title": "Full asset and token lifecycle",
    "description": "SRWAE connects asset intake, property-record review, due diligence, data rooms, legal and regulatory review, valuation, escrow/banking, issuance or listing preparation, investor access, reporting, distributions, and approved transfers."
  },
  {
    "id": "uae-pillar-3",
    "title": "Role-based access and regulatory responsibility",
    "description": "Public visitors, verified investors, sponsors, issuers, operators, licensed brokers or arrangers where applicable, custody/holder-record users, escrow or banking users, legal reviewers, compliance professionals, and other service providers receive different permissions."
  },
  {
    "id": "uae-pillar-4",
    "title": "AED-first settlement; no required platform stablecoin",
    "description": "The platform is designed around real-world asset workflows and approved bank, escrow, or settlement arrangements. It does not require a proprietary stablecoin. Payment-token functionality would be enabled only when permitted under the applicable CBUAE or financial-free-zone framework."
  }
];

const UAE_MARKET_METRICS = [
  {
    "id": "uae-metric-1",
    "value": "AED 252B",
    "title": "Dubai real-estate transactions, Q1 2026",
    "description": "Dubai Land Department reported AED 252 billion of real-estate transactions in Q1 2026 across 60,303 transactions, up 31% in value year over year. This is a transaction-flow indicator, not immediately tokenizable market value.",
    "source": "Dubai Land Department, Q1 2026 real-estate transaction data"
  },
  {
    "id": "uae-metric-2",
    "value": "AED 117B",
    "title": "Abu Dhabi real-estate transactions, H1 2026",
    "description": "Abu Dhabi Real Estate Centre reported AED 117 billion of real-estate transactions in the first half of 2026, with transaction value up 112% year over year. This demonstrates market activity but is not a tokenizable asset-stock estimate.",
    "source": "Abu Dhabi Real Estate Centre, H1 2026 transaction report"
  },
  {
    "id": "uae-metric-3",
    "value": "AED 32.2B",
    "title": "Dubai rental-contract value, Q1 2026",
    "description": "Dubai Land Department reported AED 32.2 billion in rental-contract value in Q1 2026, including 118,385 new rental contracts and 135,607 renewals, showing the depth of recurring rental and property-management workflows.",
    "source": "Dubai Land Department, Q1 2026 rental-market data"
  },
  {
    "id": "uae-metric-4",
    "value": "233K",
    "title": "Active Abu Dhabi residential leases, H1 2026",
    "description": "ADREC reported 233,000 active residential lease contracts with AED 9.3 billion of lease value in H1 2026, alongside approximately 409,000 residential units in supply.",
    "source": "Abu Dhabi Real Estate Centre, H1 2026 market report"
  }
];

const UAE_GROWTH_SIGNALS = [
  {
    "id": "uae-growth-1",
    "value": "AED 60B",
    "title": "Dubai tokenized real estate by 2033",
    "description": "Dubai Land Department projects the value of the tokenized real-estate sector could reach AED 60 billion by 2033."
  },
  {
    "id": "uae-growth-2",
    "value": "7%",
    "title": "Projected share of Dubai real-estate transactions",
    "description": "DLD states that the AED 60 billion 2033 projection would represent about seven percent of Dubai total real-estate transactions."
  },
  {
    "id": "uae-growth-3",
    "value": "$4T",
    "title": "Global tokenized real estate by 2035",
    "description": "Deloitte forecasts that the global tokenized real-estate ecosystem could grow from less than $0.3 trillion in 2024 to $4 trillion by 2035."
  },
  {
    "id": "uae-growth-4",
    "value": "2026",
    "title": "Controlled resale moves into Phase II in Dubai",
    "description": "Dubai Land Department launched Phase II of its Real Estate Tokenisation Project in February 2026, enabling resale activity within a regulated and controlled model rather than implying unrestricted liquidity."
  }
];

const UAE_MARKET_DRIVERS = [
  {
    "id": "uae-driver-1",
    "title": "Smaller investment units",
    "description": "A properly structured fractional interest can reduce the minimum capital needed for an individual investor. DLD’s initial Prypco Mint pilot allowed participation from AED 2,000 for UAE ID holders, but every SRWAE project must follow its own eligibility, offering, and jurisdiction rules."
  },
  {
    "id": "uae-driver-2",
    "title": "More flexible capital formation",
    "description": "Sponsors may seek capital against a defined legal or economic interest without selling the entire property, but the structure must match the issuer/SPV documents, property records, disclosures, and applicable regulatory pathway."
  },
  {
    "id": "uae-driver-3",
    "title": "More visible operating records",
    "description": "Standardized data rooms, property-record evidence, valuation, review records, milestone evidence, distribution reports, and transfer histories can improve transparency when the underlying data is accurate and independently verified."
  },
  {
    "id": "uae-driver-4",
    "title": "Programmable transfer controls",
    "description": "Approved systems can support wallet allowlists, investor eligibility, transfer restrictions, holding rules, identity checks, approval gates, and synchronized on-chain and off-chain records."
  },
  {
    "id": "uae-driver-5",
    "title": "Better asset discovery",
    "description": "Search, maps, standardized token codes, risk indicators, document access, jurisdiction labels, property-record status, and comparable data can help approved users evaluate opportunities more efficiently."
  },
  {
    "id": "uae-driver-6",
    "title": "Potential for controlled secondary transfers",
    "description": "Tokenization may make permitted transfers easier to administer. Dubai Phase II demonstrates a controlled resale pathway, but tokenization does not guarantee buyers, price stability, market depth, or immediate liquidity."
  }
];

const UAE_TOKENIZATION_MODELS = [
  {
    "id": "uae-model-1",
    "code": "RI",
    "title": "Rental Income",
    "description": "Represents an approved economic interest connected to rental cash flow, subject to property and issuer records, offering/issuance documents, operating expenses, reserves, financing, property performance, and distribution rules."
  },
  {
    "id": "uae-model-2",
    "code": "RS",
    "title": "Revenue Share",
    "description": "Connects investor economics to an agreed share of qualifying project or property revenue rather than automatically conveying direct ownership of the underlying property."
  },
  {
    "id": "uae-model-3",
    "code": "SP",
    "title": "Sale Profit",
    "description": "Supports projects in which the primary economic objective is a defined share of net profit after an approved property sale and related documented costs."
  },
  {
    "id": "uae-model-4",
    "code": "EA",
    "title": "Equity Appreciation",
    "description": "Represents an approved participation structure connected to increases or decreases in property, project, or issuer/SPV equity value under the controlling agreements and valuation method."
  },
  {
    "id": "uae-model-5",
    "code": "PE",
    "title": "Preferred Equity",
    "description": "Supports a preferred economic position with defined priority, return mechanics, limitations, and risk disclosures established by the issuer/SPV and controlling legal documents."
  },
  {
    "id": "uae-model-6",
    "code": "CE",
    "title": "Common Equity",
    "description": "Supports a common ownership interest in an approved entity/SPV or other permitted structure, including applicable voting, distribution, dilution, and transfer provisions."
  },
  {
    "id": "uae-model-7",
    "code": "DN",
    "title": "Debt Note",
    "description": "Represents an approved debt or financing obligation with documented principal, return/interest mechanics, maturity, collateral/security, payment priority, default, and enforcement terms."
  },
  {
    "id": "uae-model-8",
    "code": "HY",
    "title": "Hybrid",
    "description": "Combines two or more approved economic components, such as debt, preferred return, revenue share, rental income, or appreciation participation, under a documented hierarchy and waterfall."
  },
  {
    "id": "uae-model-9",
    "code": "PA",
    "title": "Pre-Auction Pool",
    "description": "Organizes approved capital commitments and acquisition workflows before an auction, with investor eligibility, bidding authority, escrow/banking, refund, and allocation rules."
  },
  {
    "id": "uae-model-10",
    "code": "PO",
    "title": "Post-Auction Token",
    "description": "Supports an acquired auction asset after purchase, including registry/title review, rehabilitation, stabilization, refinancing, leasing, sale, and investor reporting workflows."
  }
];

const UAE_INITIAL_MARKET_SEGMENTS = [
  {
    "id": "uae-segment-1",
    "title": "Documented rental portfolios",
    "description": "Small and midsize rental portfolios in Dubai or Abu Dhabi with clear ownership/registry records, leases, operating history, insurance, expenses, reserves, and property-management records can provide an understandable first use case."
  },
  {
    "id": "uae-segment-2",
    "title": "Land and construction projects",
    "description": "Registered land and approved construction projects may use milestone-based escrow or banking controls, independent engineering/inspection, controlled releases, project-status evidence, and progress reporting, subject to the applicable developer and regulatory framework."
  },
  {
    "id": "uae-segment-3",
    "title": "Pre-auction acquisition opportunities",
    "description": "Approved acquisition entities can organize time-sensitive capital, bidding limits, escrow/banking instructions, refund rules, eligibility, and post-acquisition allocation before an auction."
  },
  {
    "id": "uae-segment-4",
    "title": "Post-auction rehabilitation",
    "description": "Recently acquired properties may need registry/title resolution, repair funding, inspections, stabilization, leasing, refinancing, or sale before a complete investment strategy is established."
  },
  {
    "id": "uae-segment-5",
    "title": "Existing external token review",
    "description": "Issuers that created a token elsewhere can request document review, UAE jurisdiction mapping, data-room support, normalized display, reporting, or restricted listing/routing workflows without assuming automatic approval or UAE distribution rights."
  }
];

const UAE_CAPACITY_SCENARIOS = [
  {
    "id": "uae-capacity-1",
    "stage": "Controlled pilot",
    "activeRecords": "25",
    "averageValue": "AED 2M",
    "representedValue": "AED 50M",
    "meaning": "A small launch used to validate UAE legal templates, property-record evidence, data quality, partner workflows, investor onboarding, reporting, security, and operational controls."
  },
  {
    "id": "uae-capacity-2",
    "stage": "Dubai launch",
    "activeRecords": "250",
    "averageValue": "AED 3M",
    "representedValue": "AED 750M",
    "meaning": "A multi-partner operation serving several qualified asset owners, property types, reviewers, and approved investor groups through defined Dubai pathways."
  },
  {
    "id": "uae-capacity-3",
    "stage": "Multi-emirate scale",
    "activeRecords": "2,500",
    "averageValue": "AED 4M",
    "representedValue": "AED 10B",
    "meaning": "A scaled service with standardized onboarding, stronger automation, multiple property/regulatory pathways, licensed partners, portfolio reporting, and mature support operations."
  },
  {
    "id": "uae-capacity-4",
    "stage": "UAE platform scale",
    "activeRecords": "10,000",
    "averageValue": "AED 5M",
    "representedValue": "AED 50B",
    "meaning": "A national operating scenario requiring enterprise architecture, redundancy, independent controls, deep partner capacity, jurisdiction routing, property-record integrations, and mature compliance/support operations."
  }
];

const UAE_WORKFLOW_STEPS = [
  {
    "id": "uae-step-1",
    "number": "01",
    "title": "Discover and classify",
    "description": "Identify the asset, property registry, ownership entity, Emirate/zone, intended economics, project stage, investor audience, token rights, users, documents, payment/settlement method, and requested tokenization model."
  },
  {
    "id": "uae-step-2",
    "number": "02",
    "title": "Verify ownership and data",
    "description": "Collect and review registry/title evidence, entity records, financials, valuation, leases, project or auction information, construction/inspection, insurance, tax, identity, KYC/AML, source-of-funds, and other source records as applicable."
  },
  {
    "id": "uae-step-3",
    "number": "03",
    "title": "Structure the legal interest",
    "description": "Define the issuer/SPV, investor rights, property-title relationship, token classification, jurisdiction pathway, investor eligibility, restrictions, disclosures, transfer rules, governance, distributions, and risk allocation."
  },
  {
    "id": "uae-step-4",
    "number": "04",
    "title": "Complete professional review",
    "description": "Route the project to assigned UAE legal, regulatory/compliance, valuation, property-title/registry, escrow/banking, inspection/engineering, financial, tax/audit, custody/holder-record, and operational reviewers as required."
  },
  {
    "id": "uae-step-5",
    "number": "05",
    "title": "Prepare approved listing or offering workflow",
    "description": "Create the approved listing/data room, map view, token record, risk information, documents, jurisdiction labels, investor-eligibility rules, payment/settlement instructions, and subscription or access workflow permitted for the selected pathway."
  },
  {
    "id": "uae-step-6",
    "number": "06",
    "title": "Operate and report",
    "description": "Track funding, bank/escrow status, milestones, property activity, rental/revenue data, expenses, reserves, inspections, distributions, documents, reviews, and investor communications."
  },
  {
    "id": "uae-step-7",
    "number": "07",
    "title": "Control approved transfers",
    "description": "Apply holder eligibility, legal restrictions, licensed-partner approvals, holder/registry record updates, tax/accounting records, settlement conditions, and audit trails to permitted transfers."
  }
];

const UAE_ROLE_GROUPS = [
  {
    "id": "uae-market-users",
    "title": "Market and investor users",
    "items": [
      "Public visitor",
      "Verified investor",
      "Sponsor administrator",
      "Offering / listing administrator",
      "Marketplace administrator"
    ]
  },
  {
    "id": "uae-project-users",
    "title": "Project-side users",
    "items": [
      "Asset owner",
      "Sponsor",
      "Issuer / SPV administrator",
      "Property manager",
      "Asset manager",
      "Rehabilitation / construction manager"
    ]
  },
  {
    "id": "uae-transaction-users",
    "title": "Transaction and custody users",
    "items": [
      "Licensed broker / arranger user where applicable",
      "Custody or holder-record user",
      "Token operations user",
      "Escrow / banking user",
      "Settlement administrator"
    ]
  },
  {
    "id": "uae-professional-users",
    "title": "Professional and control users",
    "items": [
      "UAE legal counsel",
      "Regulatory / securities / virtual-asset counsel",
      "Compliance / MLRO reviewer",
      "Property title / registry reviewer",
      "Valuation reviewer",
      "Inspector / engineer",
      "Financial analyst",
      "Tax and audit professional",
      "Platform and map data administrator"
    ]
  }
];

const UAE_ACCESS_LEVELS = [
  {
    "id": "uae-access-1",
    "label": "Public access",
    "value": "Free and limited",
    "description": "Public users may view limited listing information and approximate map information without receiving precise locations, confidential property records, complete documents, review requests, investor-only data, or transaction access."
  },
  {
    "id": "uae-access-2",
    "label": "Verified access",
    "value": "Role, eligibility, and jurisdiction based",
    "description": "Verified and approved users may receive broader access according to identity, investor eligibility, jurisdiction, token rights, project/offer terms, agreements, professional role, and assigned permissions."
  },
  {
    "id": "uae-access-3",
    "label": "Project-side SaaS service",
    "value": "Monthly service plans",
    "description": "Asset owners, sponsors, issuers/SPVs, developers, and operators can select a service plan for active assets, token records, listings, data rooms, reporting, review, and workflow support."
  },
  {
    "id": "uae-access-4",
    "label": "Approved transaction service",
    "value": "Licensed arrangement pricing only",
    "description": "Any transaction, routing, arranging, brokerage, custody, settlement, or transfer-related fee is applied only where permitted under a reviewed UAE pathway and documented agreement with the appropriately authorized entity. No blanket 0.5% UAE fee is assumed."
  }
];

const UAE_RISK_CONTROLS = [
  "Tokenization does not eliminate authorization, licensing, disclosure, market-conduct, client-asset, AML/CFT, sanctions, recordkeeping, anti-fraud, investor-protection, or property-registry requirements. The exact obligations depend on the rights and jurisdiction.",
  "A token does not by itself prove property title, ownership, valuation, income, collateral, regulatory compliance, or enforceability. The controlling property registry/title records and issuer/SPV documents must be identified and reconciled.",
  "Listings, reviews, approvals, risk labels, inspections, property records, and documents can reduce information gaps but cannot eliminate investment, property, off-plan, construction, auction, market, technology, regulatory, or counterparty risk.",
  "Liquidity, distributions, appreciation, rental income, project revenue, refinancing, resale, auction success, and investment returns are never guaranteed. Controlled secondary resale does not imply an unrestricted or always-liquid market.",
  "Each UAE project may require jurisdiction-specific analysis involving DLD/VARA, DIFC/DFSA, ADGM/FSRA, SCA/local authorities, CBUAE payment rules where applicable, investor eligibility, KYC/AML and sanctions controls, tax and accounting review, custody/holder-record support, transfer restrictions, and separate partner agreements."
];

const UAE_SOURCES = [
  {
    "id": "uae-source-1",
    "title": "Dubai Land Department — Q1 2026 Real Estate Transactions",
    "note": "AED 252 billion of Q1 2026 real-estate transactions, transaction count, investment activity, and Dubai market context.",
    "href": "https://dubailand.gov.ae/en/news-media/dubai-s-real-estate-transactions-surge-31-to-reach-aed-252-billion-in-q1-2026/"
  },
  {
    "id": "uae-source-2",
    "title": "Abu Dhabi Real Estate Centre — H1 2026 Real Estate Market Report",
    "note": "AED 117 billion total transactions, 233,000 active residential leases, AED 9.3 billion lease value, residential supply, and market context.",
    "href": "https://adrec.gov.ae/en/news/press-29---adrec-releases-the-abu-dhabi-real-estate-market-report-for-h1-2026"
  },
  {
    "id": "uae-source-3",
    "title": "Dubai Land Department — Real Estate Tokenisation Project and Phase II",
    "note": "DLD’s AED 60 billion / 7% 2033 projection, official title-deed tokenisation pilot, and the 2026 move to controlled resale activity.",
    "href": "https://dubailand.gov.ae/en/news-media/dubai-land-department-launches-phase-ii-of-the-real-estate-tokenisation-project-enabling-resale-in-the-secondary-market-from-20-february/"
  },
  {
    "id": "uae-source-4",
    "title": "Virtual Assets Regulatory Authority — Virtual Asset Issuance Rulebook / ARVA Rules",
    "note": "Current Dubai virtual-asset issuance framework and Asset-Referenced Virtual Asset requirements outside DIFC.",
    "href": "https://rulebooks.vara.ae/rulebook/virtual-asset-issuance-rulebook"
  },
  {
    "id": "uae-source-5",
    "title": "UAE Federal Legislation / Securities and Commodities Authority — Virtual Assets Framework",
    "note": "Federal virtual-asset and VASP framework, local-authority coordination, and the principle that digital securities remain securities subject to securities regulation.",
    "href": "https://uaelegislation.gov.ae/en/legislations/1623"
  },
  {
    "id": "uae-source-6",
    "title": "Abu Dhabi Global Market / FSRA — Digital Assets and Digital Securities",
    "note": "ADGM framework for Virtual Assets, Digital Securities, FRTs and related regulated financial services.",
    "href": "https://www.adgm.com/business-areas/digital-assets"
  },
  {
    "id": "uae-source-7",
    "title": "Dubai Financial Services Authority — Investment Tokens / Crypto Token Framework",
    "note": "DIFC framework for Investment Tokens and updated Crypto Token regulation for financial services in or from DIFC.",
    "href": "https://www.dfsa.ae/crypto"
  }
];

const COUNTRY_CONTENT = {
  us: {
    code: 'US',
    flag: '🇺🇸',
    name: 'United States',
    selectorSubtitle: 'United States market',
    selectorDescription:
      'U.S. market data, federal and state legal context, controlled launch assumptions, and U.S. research sources.',
    panel: {
      label: 'Market position',
      headline: 'Large underlying market, disciplined initial entry',
      paragraph:
        'U.S. real estate is used as the initial market benchmark and represents tens of trillions of dollars, while tokenized real estate remains early. SRWAE is designed for broader international expansion without requiring a large share of any single national market.',
      statistic: '0.057%',
      statisticNote:
        'A $30 billion represented-asset scenario equals approximately this share of the Federal Reserve household and nonprofit real estate measure for 2026 Q1.',
    },
    platform: {
      eyebrow: 'Platform definition',
      title: 'What SRWAE is built to coordinate',
      intro:
        'The system combines marketplace discovery with controlled project, professional, legal, operational, and investor workflows. Access is determined by role, verification, offering terms, jurisdiction, agreements, and applicable law.',
      pillars: US_PLATFORM_PILLARS,
    },
    market: {
      eyebrow: 'United States market context',
      title: 'The underlying asset market is already extremely large',
      intro:
        'These figures describe the scale of selected U.S. real estate categories. They are not estimates of assets that are legally eligible, operationally ready, or likely to use SRWAE.',
      metrics: US_MARKET_METRICS,
    },
    growth: {
      eyebrow: 'Growth potential',
      title: 'Why real estate tokenization can expand quickly from a small base',
      intro:
        'Independent forecasts use different definitions and should not be added together. Taken as directional evidence, they indicate strong long-term interest in tokenized funds, loans, securitizations, land, construction projects, and other real-world assets.',
      signals: US_GROWTH_SIGNALS,
      drivers: US_MARKET_DRIVERS,
    },
    models: {
      eyebrow: 'SRWAE tokenization coverage',
      title: 'Ten supported economic and project models',
      intro:
        'Each model describes a possible economic or workflow structure. The actual legal rights come from the issuer, entity documents, offering documents, contracts, records, and applicable law—not from the model name or token code alone.',
      items: US_TOKENIZATION_MODELS,
    },
    entry: {
      eyebrow: 'Phased initial market entry',
      title: 'Where underused or locked real estate capital may enter first',
      intro:
        'The fastest practical start is not the entire global market. It is a controlled launch in the United States and a limited number of selected countries, focused on assets with understandable ownership, complete documents, measurable economics, qualified sponsors, assigned professional partners, and a clearly identified investor audience.',
      segments: US_INITIAL_MARKET_SEGMENTS,
      calloutHeadline:
        'A realistic early target is measured in qualified projects, not a percentage of all U.S. property.',
      calloutParagraph:
        'For example, one hundred qualified projects with an average represented value of $1 million would create a $100 million initial asset base. Five hundred projects averaging $1.5 million would represent $750 million. These are planning illustrations, not forecasts or fundraising promises.',
    },
    capacity: {
      eyebrow: 'Illustrative platform capacity',
      title: 'Illustrative U.S. launch capacity',
      intro:
        'The uploaded user-interface files do not prove production throughput, transaction capacity, cybersecurity readiness, or legal operating capacity. The following table is a business-planning model that shows how a very small U.S. market share can still create meaningful scale.',
      headers: [
        'Operating stage',
        'Active asset or token records',
        'Illustrative average represented asset value',
        'Illustrative total represented asset value',
        'Operational meaning',
      ],
      scenarios: US_CAPACITY_SCENARIOS,
      summary: [
        { value: '$30B', label: 'Illustrative national represented-asset scenario', ltr: true },
        { value: '0.057%', label: 'Approximate share of the 2026 Q1 Federal Reserve real estate measure', ltr: true },
        { value: 'Execution capacity', label: 'The likely binding constraint, rather than total market size', ltr: false },
      ],
      footnote:
        'Actual capacity depends on software architecture, cloud resources, database design, security testing, operational staffing, legal coverage, broker-dealer and transfer-agent relationships, escrow capacity, review turnaround, data quality, investor demand, and the number and complexity of each offering.',
    },
    workflow: {
      eyebrow: 'Controlled lifecycle',
      title: 'From real-world asset to managed digital record',
      intro:
        'SRWAE is intended to coordinate the work around an asset. It does not replace the county record, legal entity records, issuer books, transfer-agent records, contracts, professional judgment, or required regulatory filings.',
      steps: US_WORKFLOW_STEPS,
    },
    roles: {
      eyebrow: 'Role ecosystem',
      title: 'One platform, different responsibilities',
      intro:
        'A tokenized real estate system requires more than buyers and sellers. SRWAE separates public, investor, project, transaction, custody, review, legal, administrative, and reporting responsibilities.',
      groups: US_ROLE_GROUPS,
    },
    access: {
      eyebrow: 'Access and service model',
      title: 'Access expands only after verification and approval',
      intro:
        'The service and subscription forms use a role-based model. Public access is intentionally limited, while project, investor, professional, and transaction permissions require additional information and approval.',
      levels: US_ACCESS_LEVELS,
    },
    risk: {
      eyebrow: 'Legal and risk discipline',
      title: 'Tokenization improves the workflow; it does not remove the risk',
      intro:
        'In the United States, the structure and rights of each tokenized interest must be evaluated based on the actual facts, contracts, issuer records, technology model, participants, and applicable federal and state requirements.',
      controls: US_RISK_CONTROLS,
      disclaimerLabel: 'Important',
      disclaimer:
        'SRWAE is a technology and workflow platform concept. Nothing on this page is legal, tax, accounting, financial, brokerage, valuation, title, custody, or investment advice, and nothing is an offer, solicitation, recommendation, promise of liquidity, or guarantee of return.',
    },
    sources: {
      eyebrow: 'Research basis',
      title: 'Sources and methodology notes',
      intro:
        'Market figures are included to explain scale and direction. Forecasts differ in scope, geography, asset classes, definitions, and methodology. The capacity scenarios on this page were created for SRWAE planning and are not third-party forecasts.',
      items: US_SOURCES,
      researchDate: 'Research reviewed July 2026',
      buttonText: 'Open source',
    },
  },
  uae: {
    code: 'UAE',
    flag: '🇦🇪',
    name: 'United Arab Emirates',
    selectorSubtitle: 'United Arab Emirates market',
    selectorDescription:
      'UAE market data, jurisdiction-specific regulatory pathways, AED-based capacity assumptions, and UAE research sources.',
    panel: {
  "label": "Market position",
  "headline": "Deep property activity, disciplined regulated entry",
  "paragraph": "Dubai and Abu Dhabi already show large, active real-estate markets, while official Dubai initiatives are moving tokenized property from pilot testing toward controlled operational use. SRWAE UAE is designed to enter through one approved pathway first, without requiring a large share of the underlying property market.",
  "statistic": "7%",
  "statisticNote": "Dubai Land Department projects that tokenized real estate could reach AED 60 billion by 2033, representing about 7% of Dubai real-estate transactions. This is a DLD market projection, not an SRWAE forecast or promised market share."
},
    platform: {
      eyebrow: "Platform definition",
      title: "What SRWAE UAE is built to coordinate",
      intro: "The system combines marketplace discovery with controlled project, professional, legal, operational, investor, property-record, and transaction workflows. Access and available features are determined by role, verification, token rights, property registry, jurisdiction, agreements, licensed-partner responsibilities, and applicable law.",
      pillars: UAE_PLATFORM_PILLARS,
    },
    market: {
      eyebrow: "United Arab Emirates market context",
      title: "The underlying property ecosystem is already large and active",
      intro: "These figures describe selected official Dubai and Abu Dhabi real-estate activity, rental, and supply indicators. They are not estimates of assets that are legally eligible, operationally ready, or likely to use SRWAE UAE.",
      metrics: UAE_MARKET_METRICS,
    },
    growth: {
      eyebrow: "Growth potential",
      title: "Why UAE real-estate tokenization can grow from a controlled base",
      intro: "Official UAE initiatives and independent global forecasts use different definitions and should not be added together. Taken as directional evidence, they show a meaningful long-term opportunity for regulated infrastructure around tokenized property, funds, debt, project interests, and other real-world assets.",
      signals: UAE_GROWTH_SIGNALS,
      drivers: UAE_MARKET_DRIVERS,
    },
    models: {
      eyebrow: "SRWAE tokenization coverage",
      title: "Ten supported economic and project models",
      intro: "Each model describes a possible economic or workflow structure. The actual legal rights come from the issuer or SPV, property and entity records, offering or issuance documents, contracts, holder records, and applicable UAE or financial-free-zone law — not from the model name or token code alone.",
      items: UAE_TOKENIZATION_MODELS,
    },
    entry: {
      eyebrow: "Phased initial market entry",
      title: "Where well-documented UAE real-estate opportunities may enter first",
      intro: "The fastest practical start is not the entire UAE property market. It is one controlled, approved jurisdiction pathway focused on assets with clear property records, complete documents, measurable economics, qualified sponsors, assigned professional and licensed partners, and a clearly identified investor audience.",
      segments: UAE_INITIAL_MARKET_SEGMENTS,
      calloutHeadline: "A realistic early target is measured in qualified projects, not a percentage of all UAE property.",
      calloutParagraph: "For example, one hundred qualified projects with an average represented value of AED 5 million would create an AED 500 million initial asset base. Five hundred projects averaging AED 7.5 million would represent AED 3.75 billion. These are planning illustrations, not forecasts, fundraising promises, or claims of regulatory approval.",
    },
    capacity: {
      eyebrow: "Illustrative platform capacity",
      title: "Illustrative UAE launch capacity",
      intro: "The current user-interface and software materials do not prove production throughput, transaction capacity, cybersecurity readiness, regulatory authorization, or legal operating capacity. The following table is a business-planning model showing how a controlled UAE platform can reach meaningful represented-asset scale without requiring a large share of the property market.",
      headers: [
  "Operating stage",
  "Active asset or token records",
  "Illustrative average represented asset value",
  "Illustrative total represented asset value",
  "Operational meaning"
],
      scenarios: UAE_CAPACITY_SCENARIOS,
      summary: [
  {
    "value": "AED 50B",
    "label": "Illustrative UAE represented-asset scenario",
    ltr: true
  },
  {
    "value": "AED 60B / 7%",
    "label": "DLD 2033 Dubai tokenized-real-estate projection; not an SRWAE forecast",
    ltr: true
  },
  {
    "value": "Execution capacity",
    "label": "The likely binding constraint, rather than total property-market size",
    ltr: false
  }
],
      footnote: "Actual capacity depends on software architecture, cloud resources, database design, security testing, operational staffing, UAE legal and regulatory coverage, property-registry integrations, licensed-partner relationships, bank/escrow capacity, valuation and review turnaround, data quality, investor demand, and the number and complexity of each project or offering.",
    },
    workflow: {
      eyebrow: "Controlled lifecycle",
      title: "From UAE real-world asset to managed digital record",
      intro: "SRWAE UAE is intended to coordinate the work around an asset. It does not replace Dubai Land Department, ADREC or another property registry; issuer/SPV books; licensed custodian or holder records; contracts; professional judgment; bank/escrow records; or required regulatory filings.",
      steps: UAE_WORKFLOW_STEPS,
    },
    roles: {
      eyebrow: "Role ecosystem",
      title: "One platform, different responsibilities",
      intro: "A UAE tokenized real-estate system requires more than buyers and sellers. SRWAE UAE separates public, investor, project, property-record, transaction, custody/holder-record, banking/escrow, legal, compliance, administrative, and reporting responsibilities.",
      groups: UAE_ROLE_GROUPS,
    },
    access: {
      eyebrow: "Access and service model",
      title: "Access expands only after verification, eligibility, and jurisdiction approval",
      intro: "The service and subscription forms use a role-based model. Public access is intentionally limited, while project, investor, professional, property-record, and transaction permissions require additional information, verification, agreements, jurisdiction checks, and approval.",
      levels: UAE_ACCESS_LEVELS,
    },
    risk: {
      eyebrow: "Legal and risk discipline",
      title: "Tokenization improves the workflow; it does not remove the risk",
      intro: "In the UAE, the treatment of each tokenized interest depends on the actual rights, property registry, issuer/SPV records, contracts, investor audience, technology model, payment and transfer method, participants, and jurisdiction. Dubai outside DIFC, DIFC, ADGM, and other Emirates can involve different regulatory frameworks and licensed roles.",
      controls: UAE_RISK_CONTROLS,
      disclaimerLabel: "Important",
      disclaimer: "SRWAE UAE is a technology, data, and workflow platform concept. Nothing on this page is legal, regulatory, tax, accounting, financial, brokerage, arranging, custody, escrow, valuation, property-title, or investment advice, and nothing is an offer, solicitation, recommendation, statement of regulatory approval, promise of liquidity, or guarantee of return.",
    },
    sources: {
      eyebrow: "Research basis",
      title: "Sources and methodology notes",
      intro: "Market figures are included to explain scale and direction. Dubai and Abu Dhabi figures measure different periods and categories and should not be added together. Regulatory frameworks differ by jurisdiction. Capacity scenarios on this page were created for SRWAE planning and are not third-party forecasts.",
      items: UAE_SOURCES,
      researchDate: "Research reviewed August 2026",
      buttonText: 'Open source',
    },
  },
};

const COUNTRY_ORDER = ['us', 'uae'];

const AboutStarkRwaeExchange = () => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n?.resolvedLanguage || i18n?.language || 'en';
  const direction = getDirection(currentLanguage);
  const [selectedCountry, setSelectedCountry] = useState('us');
  const country = COUNTRY_CONTENT[selectedCountry] || COUNTRY_CONTENT.us;

  return (
    <main
      className={`rwae-about-page ${direction === 'rtl' ? 'rwae-about-page-rtl' : 'rwae-about-page-ltr'}`}
      dir={direction}
      lang={currentLanguage}
    >
      <section className="rwae-about-hero rwae-about-global-hero" aria-labelledby="rwae-about-title">
        <div className="rwae-about-hero-copy">
          <p className="rwae-about-eyebrow">{t('Stark RWAE Exchange')}</p>
          <h1 id="rwae-about-title">{t('A global operating system for tokenized real-world assets')}</h1>
          <p className="rwae-about-lead">
            {t(
              'SRWAE is designed as a global platform to help qualified asset owners, sponsors, issuers, operators, investors, and professional service providers structure and manage approved tokenized interests connected to land, construction, rental property, real estate equity, debt, revenue, sale profit, and auction opportunities.'
            )}
          </p>
          <p className="rwae-about-hero-note">
            {t(
              'SRWAE uses one global product architecture with market-specific data, legal workflows, roles, access controls, capacity assumptions, risks, and research. Select a country below to view the information prepared for that market.'
            )}
          </p>

          <div className="rwae-about-chip-row" aria-label={t('Platform focus areas')}>
            {[
              'Global design, phased launch',
              'Real estate and land',
              'Ten tokenization models',
              'Role-based access',
              'On-chain and off-chain records',
              'No required platform stablecoin',
            ].map((item) => (
              <span className="rwae-about-chip" key={item}>
                {t(item)}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="rwae-about-country-selector" aria-labelledby="country-selector-title">
        <div className="rwae-about-country-selector-copy">
          <p className="rwae-about-eyebrow">{t('Market selector')}</p>
          <h2 id="country-selector-title">{t('Choose a country to view its local SRWAE information')}</h2>
          <p>
            {t(
              'The platform architecture is shared globally, while market data, regulatory pathways, operating assumptions, roles, fees, risks, and research sources change by country. Select a market below. More countries can be added later.'
            )}
          </p>
        </div>

        <div className="rwae-about-country-grid" role="group" aria-label={t('Choose country')}>
          {COUNTRY_ORDER.map((countryId) => {
            const option = COUNTRY_CONTENT[countryId];
            const isSelected = selectedCountry === countryId;

            return (
              <button
                type="button"
                className={`rwae-about-country-option ${isSelected ? 'is-selected' : ''}`}
                key={countryId}
                aria-pressed={isSelected}
                onClick={() => setSelectedCountry(countryId)}
              >
                <span className="rwae-about-country-flag" aria-hidden="true" dir="ltr">
                  {option.flag}
                </span>
                <span className="rwae-about-country-option-copy">
                  <span className="rwae-about-country-option-topline">
                    <strong>{t(option.name)}</strong>
                    <span className="rwae-about-country-code" dir="ltr">{option.code}</span>
                  </span>
                  <span className="rwae-about-country-subtitle">{t(option.selectorSubtitle)}</span>
                  <span className="rwae-about-country-description">{t(option.selectorDescription)}</span>
                </span>
                <span className="rwae-about-country-check" aria-hidden="true">✓</span>
              </button>
            );
          })}
        </div>

        <p className="rwae-about-country-future-note">
          {t('Additional country markets can be added to this selector as localized content and operating pathways are prepared.')}
        </p>
      </section>

      <div className="rwae-about-country-content" key={selectedCountry}>
        <section className="rwae-about-country-overview" aria-label={t('Selected market overview')}>
          <div className="rwae-about-selected-market">
            <span className="rwae-about-selected-flag" aria-hidden="true" dir="ltr">{country.flag}</span>
            <div>
              <p className="rwae-about-selected-label">{t('Viewing market')}</p>
              <h2>{t(country.name)}</h2>
              <p>{t(country.selectorDescription)}</p>
            </div>
          </div>

          <aside className="rwae-about-hero-panel" aria-label={t('SRWAE market position')}>
            <p className="rwae-about-panel-label">{t(country.panel.label)}</p>
            <strong>{t(country.panel.headline)}</strong>
            <p>{t(country.panel.paragraph)}</p>
            <div className="rwae-about-panel-stat">
              <span className="rwae-about-number" dir="ltr">{t(country.panel.statistic)}</span>
              <small>{t(country.panel.statisticNote)}</small>
            </div>
          </aside>
        </section>

        <section className="rwae-about-section" aria-labelledby={`${selectedCountry}-platform-definition-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.platform.eyebrow)}</p>
            <h2 id={`${selectedCountry}-platform-definition-title`}>{t(country.platform.title)}</h2>
            <p>{t(country.platform.intro)}</p>
          </div>

          <div className="rwae-about-four-grid">
            {country.platform.pillars.map((pillar) => (
              <article className="rwae-about-info-card" key={pillar.id}>
                <h3>{t(pillar.title)}</h3>
                <p>{t(pillar.description)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="rwae-about-section rwae-about-market-section" aria-labelledby={`${selectedCountry}-market-size-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.market.eyebrow)}</p>
            <h2 id={`${selectedCountry}-market-size-title`}>{t(country.market.title)}</h2>
            <p>{t(country.market.intro)}</p>
          </div>

          <div className="rwae-about-metric-grid">
            {country.market.metrics.map((metric) => (
              <article className="rwae-about-metric-card" key={metric.id}>
                <div className="rwae-about-metric-value" dir="ltr">{t(metric.value)}</div>
                <h3>{t(metric.title)}</h3>
                <p>{t(metric.description)}</p>
                <small>{t(metric.source)}</small>
              </article>
            ))}
          </div>
        </section>

        <section className="rwae-about-section" aria-labelledby={`${selectedCountry}-growth-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.growth.eyebrow)}</p>
            <h2 id={`${selectedCountry}-growth-title`}>{t(country.growth.title)}</h2>
            <p>{t(country.growth.intro)}</p>
          </div>

          <div className="rwae-about-growth-grid">
            {country.growth.signals.map((signal) => (
              <article className="rwae-about-growth-card" key={signal.id}>
                <span className="rwae-about-growth-value" dir="ltr">{t(signal.value)}</span>
                <div>
                  <h3>{t(signal.title)}</h3>
                  <p>{t(signal.description)}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="rwae-about-six-grid">
            {country.growth.drivers.map((driver) => (
              <article className="rwae-about-plain-card" key={driver.id}>
                <h3>{t(driver.title)}</h3>
                <p>{t(driver.description)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="rwae-about-section rwae-about-model-section" aria-labelledby={`${selectedCountry}-models-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.models.eyebrow)}</p>
            <h2 id={`${selectedCountry}-models-title`}>{t(country.models.title)}</h2>
            <p>{t(country.models.intro)}</p>
          </div>

          <div className="rwae-about-model-grid">
            {country.models.items.map((model) => (
              <article className="rwae-about-model-card" key={model.id}>
                <span className="rwae-about-model-code" dir="ltr">{t(model.code)}</span>
                <div>
                  <h3>{t(model.title)}</h3>
                  <p>{t(model.description)}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="rwae-about-section" aria-labelledby={`${selectedCountry}-initial-entry-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.entry.eyebrow)}</p>
            <h2 id={`${selectedCountry}-initial-entry-title`}>{t(country.entry.title)}</h2>
            <p>{t(country.entry.intro)}</p>
          </div>

          <div className="rwae-about-five-grid">
            {country.entry.segments.map((segment) => (
              <article className="rwae-about-opportunity-card" key={segment.id}>
                <h3>{t(segment.title)}</h3>
                <p>{t(segment.description)}</p>
              </article>
            ))}
          </div>

          <div className="rwae-about-callout">
            <strong>{t(country.entry.calloutHeadline)}</strong>
            <p>{t(country.entry.calloutParagraph)}</p>
          </div>
        </section>

        <section className="rwae-about-section rwae-about-capacity-section" aria-labelledby={`${selectedCountry}-capacity-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.capacity.eyebrow)}</p>
            <h2 id={`${selectedCountry}-capacity-title`}>{t(country.capacity.title)}</h2>
            <p>{t(country.capacity.intro)}</p>
          </div>

          <div className="rwae-about-table-wrap" role="region" aria-label={t('Illustrative SRWAE capacity scenarios')} tabIndex="0">
            <table className="rwae-about-table">
              <thead>
                <tr>
                  {country.capacity.headers.map((header) => (
                    <th scope="col" key={header}>{t(header)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {country.capacity.scenarios.map((scenario) => (
                  <tr key={scenario.id}>
                    <th scope="row">{t(scenario.stage)}</th>
                    <td><span dir="ltr">{t(scenario.activeRecords)}</span></td>
                    <td><span dir="ltr">{t(scenario.averageValue)}</span></td>
                    <td><span dir="ltr">{t(scenario.representedValue)}</span></td>
                    <td>{t(scenario.meaning)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rwae-about-capacity-summary">
            {country.capacity.summary.map((item) => (
              <article key={`${item.value}-${item.label}`}>
                <strong dir={item.ltr ? 'ltr' : undefined}>{t(item.value)}</strong>
                <span>{t(item.label)}</span>
              </article>
            ))}
          </div>

          <p className="rwae-about-footnote">{t(country.capacity.footnote)}</p>
        </section>

        <section className="rwae-about-section" aria-labelledby={`${selectedCountry}-workflow-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.workflow.eyebrow)}</p>
            <h2 id={`${selectedCountry}-workflow-title`}>{t(country.workflow.title)}</h2>
            <p>{t(country.workflow.intro)}</p>
          </div>

          <ol className="rwae-about-workflow-list">
            {country.workflow.steps.map((step) => (
              <li className="rwae-about-workflow-item" key={step.id}>
                <span className="rwae-about-step-number" dir="ltr">{t(step.number)}</span>
                <div>
                  <h3>{t(step.title)}</h3>
                  <p>{t(step.description)}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section className="rwae-about-section" aria-labelledby={`${selectedCountry}-roles-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.roles.eyebrow)}</p>
            <h2 id={`${selectedCountry}-roles-title`}>{t(country.roles.title)}</h2>
            <p>{t(country.roles.intro)}</p>
          </div>

          <div className="rwae-about-role-grid">
            {country.roles.groups.map((group) => (
              <article className="rwae-about-role-card" key={group.id}>
                <h3>{t(group.title)}</h3>
                <ul>
                  {group.items.map((item) => (
                    <li key={item}>{t(item)}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="rwae-about-section rwae-about-access-section" aria-labelledby={`${selectedCountry}-access-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.access.eyebrow)}</p>
            <h2 id={`${selectedCountry}-access-title`}>{t(country.access.title)}</h2>
            <p>{t(country.access.intro)}</p>
          </div>

          <div className="rwae-about-access-grid">
            {country.access.levels.map((level) => (
              <article className="rwae-about-access-card" key={level.id}>
                <p className="rwae-about-access-label">{t(level.label)}</p>
                <strong>{t(level.value)}</strong>
                <p>{t(level.description)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="rwae-about-section rwae-about-risk-section" aria-labelledby={`${selectedCountry}-risk-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.risk.eyebrow)}</p>
            <h2 id={`${selectedCountry}-risk-title`}>{t(country.risk.title)}</h2>
            <p>{t(country.risk.intro)}</p>
          </div>

          <ul className="rwae-about-risk-list">
            {country.risk.controls.map((control) => (
              <li key={control}>{t(control)}</li>
            ))}
          </ul>

          <div className="rwae-about-disclaimer">
            <strong>{t(country.risk.disclaimerLabel)}</strong>
            <p>{t(country.risk.disclaimer)}</p>
          </div>
        </section>

        <section className="rwae-about-section rwae-about-source-section" aria-labelledby={`${selectedCountry}-sources-title`}>
          <div className="rwae-about-section-heading">
            <p className="rwae-about-eyebrow">{t(country.sources.eyebrow)}</p>
            <h2 id={`${selectedCountry}-sources-title`}>{t(country.sources.title)}</h2>
            <p>{t(country.sources.intro)}</p>
          </div>

          <div className="rwae-about-source-list">
            {country.sources.items.map((sourceItem) => (
              <article className="rwae-about-source-card" key={sourceItem.id}>
                <div>
                  <h3>{t(sourceItem.title)}</h3>
                  <p>{t(sourceItem.note)}</p>
                </div>
                <a href={sourceItem.href} target="_blank" rel="noreferrer noopener">
                  {t(country.sources.buttonText)}
                </a>
              </article>
            ))}
          </div>

          <p className="rwae-about-data-date">{t(country.sources.researchDate)}</p>
        </section>
      </div>
    </main>
  );
};

export default AboutStarkRwaeExchange;
